/**
 * Authentication State Manager
 * Manages authentication state and shows appropriate UI
 * Replaces Amplify Authenticator wrapper
 */

import React, { useState, useEffect } from 'react';
import { Dialog, CircularProgress, Box } from '@mui/material';
import { currentAuthenticatedUser, on } from '../../services/auth/authService';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import ForgotPasswordForm from './ForgotPasswordForm';
import MfaForm from './MfaForm';
import NewPasswordForm from './NewPasswordForm';

export default function AuthManager({ children }) {
  const [user, setUser] = useState(null);
  const [authState, setAuthState] = useState('loading'); // loading, signIn, signUp, forgotPassword, mfa, newPassword, authenticated
  const [loading, setLoading] = useState(true);
  const [mfaChallenge, setMfaChallenge] = useState(null); // Store MFA challenge result
  const [newPasswordChallenge, setNewPasswordChallenge] = useState(null); // Store NEW_PASSWORD_REQUIRED challenge
  const [challengeUsername, setChallengeUsername] = useState(null); // Store username for challenges

  // Check for existing session on mount
  useEffect(() => {
    async function checkAuth() {
      try {
        const currentUser = await currentAuthenticatedUser();
        console.log('[AuthManager] Found existing session:', currentUser.username);
        setUser(currentUser);
        setAuthState('authenticated');
      } catch (err) {
        console.log('[AuthManager] No existing session, showing login');
        setAuthState('signIn');
      } finally {
        setLoading(false);
      }
    }

    checkAuth();
  }, []);

  // Listen to auth events
  useEffect(() => {
    const unsubscribeSignIn = on('signIn', (payload) => {
      console.log('[AuthManager] Sign in event received', payload);
      setUser(payload.user);
      setAuthState('authenticated');
    });

    const unsubscribeSignOut = on('signOut', () => {
      console.log('[AuthManager] Sign out event received');
      setUser(null);
      setAuthState('signIn');
    });

    return () => {
      unsubscribeSignIn();
      unsubscribeSignOut();
    };
  }, []);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Authenticated - show app
  if (authState === 'authenticated' && user) {
    return children;
  }

  // Not authenticated - show login dialog
  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      // Prevent closing dialog when clicking outside
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          return;
        }
      }}
    >
      {authState === 'signIn' && (
        <LoginForm
          onSuccess={(user) => {
            console.log('[AuthManager] Login successful');
            setUser(user);
            setAuthState('authenticated');
          }}
          onForgotPassword={() => {
            console.log('[AuthManager] Forgot password clicked');
            setAuthState('forgotPassword');
          }}
          onSignUp={() => {
            console.log('[AuthManager] Sign up clicked');
            setAuthState('signUp');
          }}
          onMfaRequired={(challengeResult) => {
            console.log('[AuthManager] MFA required');
            setMfaChallenge(challengeResult);
            setAuthState('mfa');
          }}
          onNewPasswordRequired={(challengeResult, username) => {
            console.log('[AuthManager] New password required');
            setNewPasswordChallenge(challengeResult);
            setChallengeUsername(username);
            setAuthState('newPassword');
          }}
        />
      )}

      {authState === 'signUp' && (
        <SignUpForm
          onSuccess={(email) => {
            console.log('[AuthManager] Sign up successful for', email);
            // Automatically switch to sign in after successful verification
            setAuthState('signIn');
          }}
          onSignIn={() => {
            console.log('[AuthManager] Switching to sign in');
            setAuthState('signIn');
          }}
        />
      )}

      {authState === 'forgotPassword' && (
        <ForgotPasswordForm
          onSuccess={(accountId) => {
            console.log('[AuthManager] Password reset successful for Account ID:', accountId);
            // Automatically switch to sign in after successful reset
            setAuthState('signIn');
          }}
          onSignIn={() => {
            console.log('[AuthManager] Switching to sign in');
            setAuthState('signIn');
          }}
        />
      )}

      {authState === 'mfa' && (
        <MfaForm
          challengeResult={mfaChallenge}
          onSuccess={(user) => {
            console.log('[AuthManager] MFA verification successful');
            setUser(user);
            setMfaChallenge(null);
            setAuthState('authenticated');
          }}
          onCancel={() => {
            console.log('[AuthManager] MFA cancelled, back to sign in');
            setMfaChallenge(null);
            setAuthState('signIn');
          }}
        />
      )}

      {authState === 'newPassword' && (
        <NewPasswordForm
          challengeResult={newPasswordChallenge}
          username={challengeUsername}
          onSuccess={(user) => {
            console.log('[AuthManager] New password set successfully');
            setUser(user);
            setNewPasswordChallenge(null);
            setChallengeUsername(null);
            setAuthState('authenticated');
          }}
          onCancel={() => {
            console.log('[AuthManager] New password cancelled, back to sign in');
            setNewPasswordChallenge(null);
            setChallengeUsername(null);
            setAuthState('signIn');
          }}
        />
      )}
    </Dialog>
  );
}
